import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { GetLeadLostDetailsByLostCount } from "../Services/APIService";

import { useNavigate, useLocation } from "react-router-dom";

interface Lead {
  leadId: number;
  firstName: string;
  lastName: string | null;
  mobile: string;
  email: string;
  address: string | null;
  cityName: string | null;
  districtName: string | null;
  stateName: string | null;
  zipCode: string | null;
  sourceName: string | null;
  createdOn:string | null;
  subSourceName: string | null;
  statusName: string; // <-- Use log statusName
  reasonName?: string;
  countryCode: string | null;
  alternateMobile: string | null;
  alternateCountryCode: string | null;
}

const LeadLostDetails: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [queryParams, setQueryParams] = useState({
    leadIds: "",
    tenantId: "",
    userName: "",
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Parse URL query parameters
  useEffect(() => {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    const urlParams = new URLSearchParams(queryString);

    setQueryParams({
      leadIds: urlParams.get("leadIds") || "",
      tenantId: urlParams.get("tenantId") || "",
      userName: urlParams.get("userName") || "",
    });
  }, []);

  // Fetch lead lost details
  const fetchLeadLostDetails = async () => {
    if (!queryParams.leadIds || !queryParams.tenantId) return;

    try {
      setLoading(true);

      const leadIdsArray = queryParams.leadIds
        .split(",")
        .map((id) => Number(id));

      const response = await GetLeadLostDetailsByLostCount({
        leadIds: leadIdsArray,
        tenantId: Number(queryParams.tenantId),
      });

      const mappedLeads: Lead[] = response.map((item: any) => {
        const lead = item.lead || item;

        return {
          leadId: lead.leadId || 0,
          firstName: lead.firsT_NAME || "",
          lastName: lead.lasT_NAME || "",
          mobile: lead.mobile || "",
          email: lead.email || "",
          address: lead.address || null,
          cityName: lead.citY_NAME || null,
          districtName: lead.districT_NAME || null,
          stateName: lead.statE_NAME || null,
          zipCode: lead.ziP_CODE || null,
          sourceName: lead.sourcE_NAME || null,
          createdOn: item.createdOn
  ? (() => {
      const d = new Date(item.createdOn);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
    })()
  : null,
          subSourceName: lead.suB_SOURCE_NAME || null,
          statusName: item.statusName || "Lost",
          reasonName: item.reasonName || "N/A",
          countryCode: lead.countrY_CODE || null,
          alternateMobile: lead.alternatE_MOBILE_NO || null,
          alternateCountryCode: lead.alternatE_COUNTRY_CODE || null,
        };
      });

      setLeads(mappedLeads);
    } catch (error) {
      console.error("Error fetching lead lost details:", error);
      toast.error("Failed to load lead lost details");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (queryParams.leadIds && queryParams.tenantId) {
      fetchLeadLostDetails();
    }
  }, [queryParams]);

  const leadDashbordRedirection = (leadId: number) => {
    navigate("/LeadDashboard", { state: { leadId } });
  };

  // Filter leads based on search
  const filteredLeads = leads.filter(
    (lead) =>
      lead.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
      lead.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
      lead.mobile?.includes(searchText)
  );

  // Export to Excel
  const exportToExcel = () => {
    try {
      const headers = [
        "Lead ID",
        "Name",
        "Mobile",
        "Email",
        "Address",
        "City",
        "District",
        "State",
        "ZIP Code",
        "Source",
        "Sub Source",
        "Lead Lost Date",
        "Reason",
        "Status",
      ];

      const rows = filteredLeads.map((lead) => [
        lead.leadId,
        `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
        `${lead.countryCode || ""} ${lead.mobile || ""}`.trim() || "N/A",
        lead.email,
        lead.address || "N/A",
        lead.cityName || "N/A",
        lead.districtName || "N/A",
        lead.stateName || "N/A",
        lead.zipCode || "N/A",
        lead.sourceName || "N/A",
        lead.subSourceName || "N/A",
        lead.createdOn || "N/A",
        lead.reasonName || "N/A",
        lead.statusName,
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `lead-lost-details-${queryParams.userName}-${new Date()
          .toISOString()
          .split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Lead Lost Details - ${queryParams.userName}</title>
          <style>
            @page { size: A4 landscape; margin: 1cm; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; margin-bottom: 10px; }
            .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
            th { background-color: #a78590; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .meta-info { margin-bottom: 15px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>Lead Lost Details Report</h1>
          <div class="subtitle">User: ${queryParams.userName}</div>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            <strong>Total Records:</strong> ${filteredLeads.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>Lead ID</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Source</th>
                <th>Lead Created On</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredLeads
          .map(
            (lead) => `
                <tr>
                  <td>${lead.leadId}</td>
                  <td>${lead.firstName || ""} ${lead.lastName || ""}</td>
                  <td>${lead.mobile}</td>
                  <td>${lead.email}</td>
                  <td>${lead.address || "N/A"}</td>
                  <td>${lead.cityName || "N/A"}</td>
                  <td>${lead.stateName || "N/A"}</td>
                  <td>${lead.sourceName || "N/A"}</td>
                  <td>${lead.createdOn || "N/A"}</td>
                  <td>${lead.reasonName || "N/A"}</td>
                  <td>${lead.statusName}</td>
                </tr>
              `
          )
          .join("")}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const columns = [
    {
      name: "Name",
      cell: (row: Lead) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.firstName?.charAt(0).toUpperCase() || "?"}
          </div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              leadDashbordRedirection(row.leadId);
            }}
            style={{
              textDecoration: "none",
              fontWeight: 500,
              color: "#33475b",
            }}
          >
            {`${row.firstName || ""} ${row.lastName || ""}`.trim() || "N/A"}
          </a>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Mobile",
      cell: (row: Lead) => (
        <span>
          {`${row.countryCode || ""} ${row.mobile || ""}`.trim() || "N/A"}
        </span>
      ),
      sortable: true,
      width: "150px",
    },
    // { name: "Email", selector: (row: Lead) => row.email, sortable: true, width: "200px" },
    // { name: "Address", selector: (row: Lead) => row.address || "N/A", sortable: true, width: "180px" },
    // { name: "City", selector: (row: Lead) => row.cityName || "N/A", sortable: true, width: "120px" },
    // { name: "District", selector: (row: Lead) => row.districtName || "N/A", sortable: true, width: "120px" },
    // { name: "State", selector: (row: Lead) => row.stateName || "N/A", sortable: true, width: "120px" },
    { name: "Lead Lost Date", selector: (row: Lead) => row.createdOn || "N/A", sortable: true, width: "100px" },
    { name: "Source", selector: (row: Lead) => row.sourceName || "N/A", sortable: true, width: "120px" },
    { name: "Sub Source", selector: (row: Lead) => row.subSourceName || "N/A", sortable: true, width: "140px" },
    {
      name: "Reason",
      selector: (row: Lead) => row.reasonName || "N/A",
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      cell: (row: Lead) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "12px",
            fontWeight: "600",
            fontSize: "13px",
            backgroundColor: "#fce4ec",
            color: "#ad1457",
          }}
        >
          {row.statusName}
        </span>
      ),
      width: "100px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster position="top-center" reverseOrder={false} containerStyle={{ zIndex: 10000 }} />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div>
                        <div className="card-title mb-0">Lead Lost Details</div>
                        <small style={{ color: "#666" }}>User: {queryParams.userName}</small>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Name, Email or Mobile"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button className="demo-btn btn btn-outline-success btn-sm" onClick={exportToPDF}>
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button className="demo-btn btn btn-outline-success btn-sm" onClick={exportToExcel}>
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-secondary btn-sm"
                          onClick={() => window.close()}
                        >
                          <i className="bi bi-x-circle"></i> Close
                        </button>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredLeads}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No lead lost data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader loading={loading} message="Loading Lead Lost Details" submessage="Please wait..." />

      <style>{`
        .rdt_TableCol:first-of-type { padding-left: 16px !important; }
        .datatableSearchInput { border: 1px solid #dee2e6; border-radius: 4px; font-size: 14px; }
        .datatableSearchInput:focus { outline: none; border-color: #a78590; box-shadow: 0 0 0 0.2rem rgba(167, 133, 144, 0.25); }
      `}</style>
    </div>
  );
};

export default LeadLostDetails;
